import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { DotsThreeOutlineVertical } from '@liveauctioneers/hammer-ui-core/icons';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Menu, MenuItem } from '@liveauctioneers/hammer-ui-core/menu';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import { useCanRetractTimedPlusAuctionBid } from '@/hooks/timedPlus/retractBid/useCanRetractTimedPlusAuctionBid';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryCatalogId,
    getItemSummaryCatalogStatus,
    getItemSummaryIsTimedPlusAuction,
    getItemSummarySellerId,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { CompleteAccountModalContext, openExcludeHouseModal, openRetractBidModal } from '@/redux/modules/modal';
import {
    createSaveSearchKeyBySearchParams,
    deleteSavedSearch,
    getIsSavedSearch,
    submitSaveSearch,
} from '@/redux/modules/saveSearch';
import { SearchUrlStatus } from '@/types/search/enums/searchUrlStatus';
import { ItemCardMenuClickTrigger, useTrackItemCardMenuClickAnalytics } from '@/utils/amplitude/analytics';
import noop from '@/utils/noop';
import { DEFAULT_STATUS_FOR_ONLINE_SEARCH } from '@/utils/queryParams';
import { getSearchUrlWithParams } from '@/utils/urls';

export type ItemCardMenuProps = {
    itemId: number;
    showIcon?: boolean;
};

const ItemCardMenu = ({ itemId, showIcon = true }: ItemCardMenuProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { formatMessage } = useIntl();

    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const isTimedPlus = useAppSelector((state) => getItemSummaryIsTimedPlusAuction(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemSummaryCatalogStatus(state, itemId));
    const canRetractTimedPlusAuctionBid = useCanRetractTimedPlusAuctionBid(itemId);

    const trackItemCardMenuClickAnalytics = useTrackItemCardMenuClickAnalytics();

    const [canRetractBid, setCanRetractBid] = useState(false);
    const { approved, registered } = useAppSelector((state) => getCatalogRegistration(state, catalogId));

    useEffect(() => {
        const hasBid = (approved || registered) && bidderHasBid;
        const canRetractBasedOnCatalogType = isTimedPlus ? canRetractTimedPlusAuctionBid : catalogStatus !== 'live';
        setCanRetractBid(hasBid && canRetractBasedOnCatalogType);
    }, [approved, bidderHasBid, canRetractTimedPlusAuctionBid, catalogStatus, isTimedPlus, registered]);

    const showConfirmExcludeModal = useCallback(() => {
        trackItemCardMenuClickAnalytics({
            houseId: sellerId,
            itemId,
            trigger: ItemCardMenuClickTrigger.ExcludeHouseFromSearch,
        });
        dispatch(openExcludeHouseModal(sellerId));
    }, [dispatch, sellerId, trackItemCardMenuClickAnalytics, itemId]);

    const saveSearchParams = useMemo(() => ({ keyword: title, status: DEFAULT_STATUS_FOR_ONLINE_SEARCH }), [title]);

    const itemSearchKeyword = saveSearchParams.keyword;
    const saveSearchKey = createSaveSearchKeyBySearchParams(saveSearchParams);
    const isSavedSearch = useAppSelector((state) => getIsSavedSearch(state, saveSearchKey));

    const toggleSearchAlert = useCallback(() => {
        if (isSavedSearch) {
            dispatch(deleteSavedSearch(saveSearchParams));
            toast('success', {
                description: formatMessage({ id: 'searchRemoveAlertToast' }),
                title: formatMessage({ id: 'toastMessages.titles.success' }),
            });
        } else {
            dispatch(submitSaveSearch(saveSearchParams, true));
            toast('success', {
                description: formatMessage({ id: 'searchCreateAlertToast' }),
                title: formatMessage({ id: 'toastMessages.titles.success' }),
            });
        }
    }, [dispatch, formatMessage, isSavedSearch, saveSearchParams]);

    const onSeeSimilarItemsClicked = useCallback(() => {
        trackItemCardMenuClickAnalytics({
            houseId: sellerId,
            itemId,
            trigger: ItemCardMenuClickTrigger.SeeSimilarItems,
        });
        navigate(
            getSearchUrlWithParams({
                keyword: itemSearchKeyword,
                page: 1,
                status: SearchUrlStatus.Online,
            })
        );
    }, [itemSearchKeyword, itemId, navigate, sellerId, trackItemCardMenuClickAnalytics]);

    const onRetractBidClicked = useCallback(() => {
        trackItemCardMenuClickAnalytics({
            houseId: sellerId,
            itemId,
            trigger: ItemCardMenuClickTrigger.RetractBid,
        });
        dispatch(openRetractBidModal(itemId));
    }, [dispatch, itemId, sellerId, trackItemCardMenuClickAnalytics]);

    return (
        <Menu
            hasRoundedBorders
            menuPlacement="left"
            menuSize="md"
            numberOfColumns={1}
            trigger={
                <span
                    className={cn('flex cursor-pointer justify-center', {
                        'opacity-0': !showIcon,
                        'opacity-100': showIcon,
                    })}
                    data-testid={`item-card-${itemId}-ellipsis`}
                >
                    <IconButton
                        aria-label={`Menu options for ${title}`}
                        className="text-text-primary hover:text-link-primary"
                        hideTooltip
                        icon={DotsThreeOutlineVertical}
                        iconStyle="fill"
                        size="sm"
                        variant="transparent"
                    />
                </span>
            }
        >
            <MenuItem
                onClick={onSeeSimilarItemsClicked}
                primaryLabel={formatMessage({ id: 'search_see_similar' })}
            />
            <ShouldLogInContainer
                modalContext={{
                    id: 1,
                    runAtLoginFunction: toggleSearchAlert,
                    type: CompleteAccountModalContext.LoginAndRunFunction,
                }}
            >
                <MenuItem
                    onClick={noop}
                    primaryLabel={formatMessage({
                        id: isSavedSearch ? 'search_remove_alert' : 'search_create_alert',
                    })}
                />
            </ShouldLogInContainer>
            <ShouldLogInContainer
                modalContext={{
                    id: 2,
                    runAtLoginFunction: showConfirmExcludeModal,
                    type: CompleteAccountModalContext.LoginAndRunFunction,
                }}
            >
                <MenuItem
                    onClick={noop}
                    primaryLabel={formatMessage({ id: 'search_exclude_this_auction_house' })}
                />
            </ShouldLogInContainer>
            {canRetractBid && (
                <MenuItem
                    onClick={onRetractBidClicked}
                    primaryLabel={formatMessage({ id: 'retract_bid' })}
                />
            )}
        </Menu>
    );
};

export default ItemCardMenu;
